import { Disclosure, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { ReactNode, forwardRef, useCallback, useState } from 'react';
import { ChevronDownIcon } from '../icons';
import { SubduedTextSet } from './SubduedTextSet';

interface DisclosureListItemProps {
  leftContent?: ReactNode;
  leftMainString?: ReactNode;
  leftSubduedContentSet?: ReactNode[];
  leftSubduedContent?: ReactNode;
  rightContent?: ReactNode;
  rightAdditionalContent?: ReactNode;
  rightMainString?: ReactNode;
  rightSubduedString?: ReactNode;
  rightTopSubduedString?: ReactNode;
  rightFlexStartSubduedString?: ReactNode;
  rightLeftContent?: ReactNode;
  bottomContent?: Array<ReactNode[]>;
  bottomCustomContent?: ReactNode;
  contentGridColumns?: string;
  classes?: {
    container?: string;
    openerArrow?: string;
    leftContent?: string;
    leftMainString?: string;
    rightContent?: string;
    rightContentUp?: string;
    rightMainString?: string;
    content?: string;
    leftSubduedContentSetItem?: string;
  };
  onOpen?: () => void;
  onClose?: () => void;
  lastItem?: boolean;
  onClick?: () => void;
  hoverTitle?: string;
}

export const DisclosureListItem = forwardRef<HTMLDivElement, DisclosureListItemProps>(
  (
    {
      classes,
      leftContent,
      rightContent,
      bottomContent,
      bottomCustomContent,
      leftMainString,
      rightMainString,
      rightSubduedString,
      rightFlexStartSubduedString,
      rightLeftContent,
      rightAdditionalContent,
      leftSubduedContentSet,
      leftSubduedContent,
      rightTopSubduedString,
      contentGridColumns,
      onOpen,
      onClose,
      lastItem = false,
      onClick,
      hoverTitle,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const handleToggleOpen = useCallback(() => {
      setIsOpen(open => {
        !open && onOpen && onOpen();
        open && onClose && onClose();
        return !open;
      });
    }, [onClose, onOpen]);

    const isPossibleOpen = !!bottomContent?.length || bottomCustomContent;
    return (
      <div
        className={classNames(
          classes?.container,
          'w-full flex flex-col border-gray-200',
          'sm:px-6 sm:pt-4 sm:border sm:rounded-xl',
          {
            'border-b': !lastItem,
            'hover:bg-gray-50 hover:cursor-pointer': !!onClick,
          },
        )}
        onClick={onClick}
        title={hoverTitle}
      >
        <div
          ref={ref}
          className={classNames(
            'w-full items-center pb-2 sm:pb-4',
            { 'cursor-pointer': isPossibleOpen },
            contentGridColumns ? 'grid gap-1' : 'flex justify-between',
            classes?.content,
          )}
          style={contentGridColumns ? { gridTemplateColumns: contentGridColumns } : {}}
          onClick={isPossibleOpen ? handleToggleOpen : undefined}
        >
          <div className={classNames('flex gap-2', classes?.leftContent)}>
            {leftContent}
            <div className="flex flex-col items-start w-full">
              <div
                className={classNames(
                  'text-sm leading-[22px] first-letter:capitalize break-words',
                  classes?.leftMainString,
                )}
                style={{ wordBreak: 'break-word' }}
              >
                {leftMainString}
              </div>
              {leftSubduedContentSet && (
                <SubduedTextSet
                  set={leftSubduedContentSet}
                  classNameItem={classes?.leftSubduedContentSetItem}
                />
              )}
              <div>{leftSubduedContent}</div>
            </div>
          </div>

          {rightLeftContent}
          <div
            className={classNames(
              'grid sm:grid-columns-3 gap-1 sm:gap-4 items-center',
              classes?.rightContentUp,
            )}
          >
            {rightContent}
            {rightFlexStartSubduedString === undefined || rightFlexStartSubduedString === null
              ? (rightMainString || rightSubduedString || rightTopSubduedString) && (
                  <div
                    className={classNames(
                      'flex flex-col items-end',
                      {
                        'sm:w-[125px]': !classes?.rightMainString?.includes('w-'),
                        'sm:col-start-2 sm:col-end-3': !classes?.rightMainString?.includes('col-'),
                      },
                      classes?.rightMainString,
                    )}
                  >
                    <span className="text-[12px] leading-[20px] text-gray-500">
                      {rightTopSubduedString}
                    </span>
                    <span className="text-[14px] leading-[22px]">{rightMainString}</span>
                    <span className="text-[12px] leading-[20px] text-gray-500">
                      {rightSubduedString}
                    </span>
                  </div>
                )
              : (rightMainString || rightFlexStartSubduedString) && (
                  <div
                    className={classNames(
                      'flex flex-col items-end sm:col-start-2 sm:col-end-3 sm:w-[120px]',
                      classes?.rightMainString,
                    )}
                  >
                    <div>
                      <span className="text-[14px] leading-[22px]">{rightMainString}</span>
                      <span className="text-[12px] leading-[20px] text-gray-500">
                        {rightFlexStartSubduedString}
                      </span>
                    </div>
                  </div>
                )}
            {(isPossibleOpen || rightAdditionalContent) && (
              <div
                className={classNames(
                  'items-center justify-center w-[40px] h-[40px] pl-4 sm:border-l border-gray-200 hidden sm:flex justify-self-end',
                  {
                    '!flex': rightAdditionalContent,
                    'sm:col-start-3 sm:col-end-4': !classes?.rightContent?.includes('col-'),
                  },
                  classes?.rightContent,
                )}
              >
                {rightAdditionalContent}
                {isPossibleOpen && (
                  <img
                    src={ChevronDownIcon}
                    alt="open"
                    className={classNames(
                      'select-none hover:opacity-80',
                      { 'rotate-180': isOpen },
                      classes?.openerArrow,
                    )}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        {isPossibleOpen && (
          <Disclosure>
            <Transition
              show={isOpen}
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel static>
                {bottomCustomContent || (
                  <div className="w-full grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 justify-between py-4 border-t border-gray-200">
                    {bottomContent
                      ?.filter(item => !!item[1])
                      .map(([title, value], index) => (
                        <div className="flex flex-col gap-2" key={index}>
                          <span className="text-[12px] leading-[20px] text-gray-500">{title}</span>
                          <span className="text-[14px] leading-[22px]">{value}</span>
                        </div>
                      ))}
                  </div>
                )}
              </Disclosure.Panel>
            </Transition>
          </Disclosure>
        )}
      </div>
    );
  },
);
